import React, { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { Loader2 } from "lucide-react";
import VideoCard from "../VideoCard";
import SmartVideoPlayer from "../SmartVideoPlayer";
import SkeletonGrid from "../SkeletonGrid";
import Context from "../../../../../Context/Context";

/**
 * VideoContent Component with Infinite Scroll Pagination
 *
 * Features:
 * - Shows 20 videos initially
 * - Loads 20 more videos when scrolling to bottom
 * - Optimized for performance with useMemo
 * - Handles video deletion and updates
 * - Supports different view modes (grid, list, masonry)
 * - Shows loading indicators and progress
 * - Resets pagination when videos or view mode changes
 */
export const VideoContent = ({
  error,
  loading,
  view,
  filteredVideos,
  selectedVideo,
  handleVideoClick,
  handleClosePlayer,
  scrollableDivRef,
  selectedPlaylist,
  setVideos,
  viewMode,
  setViewMode,
  searchTerm,
}) => {
  const Ctx = useContext(Context);
  const isDarkMode = Ctx.isDarkMode ?? true;
  const [localVideos, setLocalVideos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const ITEMS_PER_PAGE = 20;

  // Update local videos when filteredVideos, selectedPlaylist, or searchTerm changes
  useEffect(() => {
    // If there's a search term, prioritize showing filtered videos regardless of playlist
    const videos = (searchTerm && searchTerm.trim() !== "") ? filteredVideos : (selectedPlaylist ? selectedPlaylist.videos : filteredVideos);
    setLocalVideos(videos || []);
    // Reset pagination when videos change
    setCurrentPage(1);
    setHasMore(true);
    setIsLoadingMore(false);
  }, [selectedPlaylist, filteredVideos, searchTerm]);

  // Reset pagination when viewMode changes to ensure proper layout
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setIsLoadingMore(false);
  }, [viewMode]);

  // Memoized displayed videos calculation
  const { displayedVideos, hasMoreVideos } = useMemo(() => {
    const startIndex = 0;
    const endIndex = currentPage * ITEMS_PER_PAGE;
    const newDisplayedVideos = localVideos.slice(startIndex, endIndex);
    const hasMoreVideos = endIndex < localVideos.length;

    return {
      displayedVideos: newDisplayedVideos,
      hasMoreVideos,
    };
  }, [localVideos, currentPage, ITEMS_PER_PAGE]);

  // Update hasMore state when displayed videos change
  useEffect(() => {
    setHasMore(hasMoreVideos);
  }, [hasMoreVideos]);

  // Function to handle video updates
  const handleVideoUpdate = (videoUrl) => {
    setLocalVideos((prevVideos) => {
      const updatedVideos = prevVideos.filter((v) => v.videoUrl !== videoUrl);
      return updatedVideos;
    });
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollableDivRef.current || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollableDivRef.current;
    const threshold = 100; // Load more when 100px from bottom

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadMoreVideos();
    }
  }, [isLoadingMore, hasMore, scrollableDivRef]);

  // Load more videos function
  const loadMoreVideos = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    // Simulate loading delay for better UX
    setTimeout(() => {
      setCurrentPage((prev) => prev + 1);
      setIsLoadingMore(false);
    }, 500);
  }, [isLoadingMore, hasMore]);

  // Add scroll event listener
  useEffect(() => {
    const scrollElement = scrollableDivRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className={`mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>⚠️</div>
          <p className={`text-lg ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>{error}</p>
        </div>
      </div>
    );
  }

  if (view === "grid") {
    return (
      <div className="h-full flex flex-col">
        {/* Video Grid */}
        <div
          ref={scrollableDivRef}
          className={`
            flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-6 scrollbar-hide
            ${viewMode === 'grid' && 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5'}
            ${viewMode === "list" && "space-y-3 md:space-y-4"}
            ${viewMode === "masonry" &&
            "columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 md:gap-5"
            }
          `}
        >
          {loading && displayedVideos.length === 0 ? (
            <SkeletonGrid viewMode={viewMode} />
          ) : displayedVideos.length > 0 ? (
            <>
              {displayedVideos.map((video, index) => (
                <div
                  key={`${video.videoUrl}-${index}`}
                  className={
                    viewMode === "masonry" ? "break-inside-avoid mb-4" : ""
                  }
                >
                  <VideoCard
                    video={video}
                    onClick={handleVideoClick}
                    setVideos={setVideos}
                    onVideoUpdate={handleVideoUpdate}
                    viewMode={viewMode}
                  />
                </div>
              ))}

              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="w-full flex justify-center items-center py-4">
                  <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>
                      Loading more videos... ({displayedVideos.length} of{" "}
                      {localVideos.length})
                    </span>
                  </div>
                </div>
              )}

              {/* End of results indicator */}
              {!hasMore && displayedVideos.length > 0 && (
                <div className="w-full flex justify-center items-center py-4">
                  <div className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {localVideos.length > 0
                      ? `Showing all ${localVideos.length} videos`
                      : "No more videos to load"}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center h-64">
              <div className={`mb-4 text-4xl ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>🎬</div>
              <p className={`text-base md:text-lg mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                {selectedPlaylist
                  ? `No videos in ${selectedPlaylist.playlistName}`
                  : "No videos found"}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {selectedPlaylist
                  ? "This playlist is empty"
                  : "Try adjusting your search or selecting a different playlist"}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0">
      <SmartVideoPlayer video={selectedVideo} onClose={handleClosePlayer} />
    </div>
  );
};

export default VideoContent;
